import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import {
  TBctxItemWithoutSize,
  TBctxItemWithoutSizeAndBctxKey,
  TBulkEventWithoutStatus,
  TBundleQcBgDBLockedBy,
  TBundleQcBgDBLockResult,
  TEventAddtionSideActions,
  TIncompleteBundleAdditionDTO,
  TIncompleteBundleResult,
  TRemoteDeploymentCheck,
  TRemoteDeploymentCheckWithEventsSyncResult,
  TSelectedBctxInfo,
  TStationDetails,
  TUserAction,
} from '@monorepo/types';
import { BCTXComputationHelper, EventUtil, InputAppErrorDisplay } from '@monorepo/utils';
import { select, Store } from '@ngrx/store';
import produce from 'immer';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { getIncompleteBundlesInfoForCurrentStationAndShift } from '../input-app-client-db/input-app-client-db.query';
import { TInputAppState } from '../reducer';
import {
  AcquireLockAndSyncIncompleteBundles,
  ReleaseBundleQcBgDBLock,
  UpdateBundleQcBgDBLockDetails,
} from './bundle-qc.actions';
import {
  getActiveBctxList,
  getActiveBctxMapBySearchId,
  getBundleQcBgDBLockDetails,
  getCurrentBctxCounts,
  getUpdatedAtAndLastSyncedAt,
} from './bundle-qc.query';
import { getOnlineStatus } from '../metadata/metadata.query';
@Injectable({
  providedIn: 'root',
})
export class BundleQcDBFacade {
  private appUpdateSubscription = new Subject<TRemoteDeploymentCheckWithEventsSyncResult>();
  constructor(
    private store: Store<TInputAppState>,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private alertCtrl: AlertController,
    private inputAppErrorDisplayUtil: InputAppErrorDisplay,
  ) {}

  getAppUpdateSubscription() {
    return this.appUpdateSubscription.asObservable();
  }

  emitAppUpdateSubscription(remoteDeploymentCheckWithSyncResult: TRemoteDeploymentCheckWithEventsSyncResult) {
    this.appUpdateSubscription.next(remoteDeploymentCheckWithSyncResult);
  }

  getBgDBLockDetails() {
    return this.store.pipe(select(getBundleQcBgDBLockDetails));
  }

  acquireBgDBLock(lockFor: TBundleQcBgDBLockedBy): Observable<TBundleQcBgDBLockResult> {
    return this.getBgDBLockDetails().pipe(
      take(1),
      map((lockDetails) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${lockDetails.lockedBy} is happening, please try again`,
          };
        } else {
          this.store.dispatch(
            new UpdateBundleQcBgDBLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
          };
        }
      }),
    );
  }

  getIncompleteBundlesInfoForCurrentStationAndShift() {
    return this.store.pipe(select(getIncompleteBundlesInfoForCurrentStationAndShift));
  }

  getActiveBctxList() {
    return this.store.pipe(select(getActiveBctxList));
  }

  getActiveBctxMapBySearchId() {
    return this.store.pipe(select(getActiveBctxMapBySearchId));
  }

  validateBundle(bctxKey: string) {
    return this.getActiveBctxList().pipe(
      take(1),
      map((list) => {
        return !!list.find((listInfo) => listInfo.bctxKey === bctxKey);
      }),
    );
  }

  async showCompletedBundleAndClear(selectedBctx: TBctxItemWithoutSizeAndBctxKey) {
    const alert = await this.alertCtrl.create({
      backdropDismiss: false,
      header: 'Warning',
      subHeader: `Selected bundle already complete, do you want to delete this?`,
      buttons: [
        {
          text: 'No',
        },
        {
          text: 'Yes',
          handler: () => {
            this.clearBundleAndUpdatedList(selectedBctx);
          },
        },
      ],
    });
    alert.present();
  }

  getFormattedIncompleteBundleInfo(): Observable<TBctxItemWithoutSizeAndBctxKey[]> {
    return this.getIncompleteBundlesInfoForCurrentStationAndShift().pipe(
      map((res) => {
        return res.list.map(({ ctx, g_ctx_key }) => {
          return {
            list: ctx.map(({ k, v }) => {
              return { k, v } as TBctxItemWithoutSize;
            }),
            bctxKey: g_ctx_key,
          };
        });
      }),
    );
  }

  getCurrentBctxCounts() {
    return this.store.pipe(select(getCurrentBctxCounts));
  }

  getIncompleteBundleInfoAndLastSyncedAt() {
    return this.store.pipe(select(getUpdatedAtAndLastSyncedAt));
  }

  validateAndSyncIncompleteBundlesForCurrentStation(userAction: TUserAction = {}) {
    this.inputAppClientDBFacade
      .getStationDetails()
      .pipe(take(1))
      .subscribe((res) => {
        this.validateAndSyncIncompleteBundles(res, userAction);
      });
  }

  validateAndSyncIncompleteBundles(stationDetails: TStationDetails, userAction: TUserAction = {}) {
    this.getIncompleteBundleInfoAndLastSyncedAt()
      .pipe(take(1))
      .subscribe((res) => {
        if (res.lastSyncedAt !== res.lastUpdatedAt || userAction.chainWithAppUpdate) {
          this.store.dispatch(new AcquireLockAndSyncIncompleteBundles(stationDetails, userAction));
        }
      });
  }

  getOnlineStatus() {
    return this.store.pipe(select(getOnlineStatus));
  }

  pollForBgDBLock(userAction: TUserAction, hasLoaderShown = false) {
    this.acquireBgDBLock('SYNCING_COMPLETE_BUNDLES')
      .pipe(take(1))
      .subscribe(async (bgDBLockDetails) => {
        if (bgDBLockDetails.status === 'SUCCESS') {
          if (hasLoaderShown) await this.inputAppErrorDisplayUtil.dismissLoading();
          this.store.dispatch(new ReleaseBundleQcBgDBLock());
          if (userAction.chainWithAppUpdate) {
            this.forceSyncBundlesAndUpdateApp(userAction.chainWithAppUpdate, true);
          }
        } else {
          if (!hasLoaderShown)
            await this.inputAppErrorDisplayUtil.presentLoading('Evaluating pending defective images...');
          setTimeout(() => {
            this.pollForBgDBLock(userAction, true);
          }, 2000);
        }
      });
  }

  forceSyncBundlesAndUpdateApp(remoteDeploymentcheck: TRemoteDeploymentCheck, isBgDBUnlocked: boolean) {
    this.getOnlineStatus()
      .pipe(take(1))
      .subscribe((isOnline) => {
        if (!isOnline) {
          this.emitAppUpdateSubscription({
            remoteDeploymentcheck,
            syncResult: {
              status: 'NO_INTERNET_FOR_SYNCING_PENDING_EVENTS',
            },
          });
        } else if (!isBgDBUnlocked) {
          this.pollForBgDBLock({ chainWithAppUpdate: remoteDeploymentcheck }, false);
        } else {
          this.validateAndSyncIncompleteBundlesForCurrentStation({ chainWithAppUpdate: remoteDeploymentcheck });
        }
      });
  }

  fetchAvailableWipAndValidateAndThenAddBundle(bctxInfo: TBctxItemWithoutSizeAndBctxKey) {
    return this.inputAppClientDBFacade.getBctxWipMap().pipe(
      take(1),
      switchMap((res) => {
        if (res[bctxInfo.bctxKey]) {
          return this.getUpdatedIncompleteBundlesInfo([
            {
              list: bctxInfo.list,
              size: Object.values(res[bctxInfo.bctxKey].sizeToWipMap)[0].name,
              bctxKey: bctxInfo.bctxKey,
            },
          ]).pipe(
            take(1),
            map((list) => {
              const result: TIncompleteBundleResult = {
                status: 'SUCCESS',
                list,
              };
              return result;
            }),
          );
        } else {
          const result: TIncompleteBundleResult = {
            status: 'FAILURE',
          };
          return of(result);
        }
      }),
    );
  }

  getUpdatedIncompleteBundlesInfo(bctxList: TIncompleteBundleAdditionDTO[]) {
    return this.getIncompleteBundlesInfoForCurrentStationAndShift().pipe(
      take(1),
      switchMap((incompleteBundlesInfo) => {
        return this.inputAppClientDBFacade.getBctxWipMap().pipe(
          take(1),
          map((bctxWipMap) => {
            return [incompleteBundlesInfo, bctxWipMap] as const;
          }),
        );
      }),
      switchMap(([incompleteBundlesInfo, bctxWipMap]) => {
        return this.inputAppClientDBFacade.getBctxViz().pipe(
          take(1),
          map((bctxViz) => [incompleteBundlesInfo, bctxWipMap, bctxViz] as const),
        );
      }),
      map(([incompleteBundlesInfo, bctxWipMap, bctxViz]) => {
        bctxList.forEach((bctxInfo) => {
          const initialQty = bctxWipMap[bctxInfo.bctxKey]
            ? Object.values(bctxWipMap[bctxInfo.bctxKey].sizeToWipMap)[0].wip_qty
            : 0;
          const index = incompleteBundlesInfo.list.findIndex(({ g_ctx_key }) => g_ctx_key === bctxInfo.bctxKey);
          if (index === -1) {
            const search_id = BCTXComputationHelper.getValueByKeyInBctxItems(bctxInfo.list, 'search_id');
            if (search_id) {
              const size = bctxInfo.size;
              incompleteBundlesInfo = produce(incompleteBundlesInfo, (draft) => {
                draft.list.push({
                  g_ctx_key: bctxInfo.bctxKey,
                  ctx: BCTXComputationHelper.getBctxContexts(bctxInfo.list, bctxViz),
                  bctx_key: bctxInfo.bctxKey,
                  size_list: [{ name: size, qty: initialQty, updated: false, target_qty: 0 }],
                  datetime: new Date().getTime(),
                  remove: false,
                  status: true,
                  subject: null,
                  values: {
                    smv: 0,
                    operator: 0,
                    helper: 0,
                    qty: initialQty,
                    efficiency: 0,
                  },
                  src_ids: {
                    search_id,
                  },
                });
                draft.lastUpdatedAt = new Date().getTime();
              });
            }
          } else if (
            incompleteBundlesInfo.list[index].size_list &&
            incompleteBundlesInfo.list[index].size_list[0] &&
            incompleteBundlesInfo.list[index].size_list[0].qty < initialQty
          ) {
            //if from defective listing page, user inserts to incomplete bundles, then initial qty might be zero
            //in these cases we update the initial qty
            incompleteBundlesInfo = produce(incompleteBundlesInfo, (draft) => {
              draft.list[index].size_list[0].qty = initialQty;
              draft.list[index].values.qty = initialQty;
              draft.lastUpdatedAt = new Date().getTime();
            });
          }
        });
        return incompleteBundlesInfo;
      }),
    );
  }

  getClearedIncompleteBundlesInfo(bctxInfo: TBctxItemWithoutSizeAndBctxKey) {
    return this.getIncompleteBundlesInfoForCurrentStationAndShift().pipe(
      take(1),
      map((incompleteBundlesInfo) => {
        const index = incompleteBundlesInfo.list.findIndex(({ g_ctx_key }) => g_ctx_key === bctxInfo.bctxKey);
        if (index > -1) {
          incompleteBundlesInfo = produce(incompleteBundlesInfo, (draft) => {
            draft.list.splice(index, 1);
            draft.lastUpdatedAt = new Date().getTime();
          });
        }
        return incompleteBundlesInfo;
      }),
    );
  }

  clearBundleAndUpdatedList(bctxInfo: TBctxItemWithoutSizeAndBctxKey) {
    return this.getClearedIncompleteBundlesInfo(bctxInfo)
      .pipe(take(1))
      .subscribe((res) => {
        this.inputAppClientDBFacade.updateIncompleteBundlesInfo(res);
      });
  }

  getCompleteEvent(selectedBctxInfo: TSelectedBctxInfo, bundleQty: number) {
    const eventTime = new Date().getTime();
    return this.inputAppClientDBFacade.getBulkEventMetadata(eventTime, 'ACROSS_BCTX').pipe(
      take(1),
      map((completeEventMetadata) => {
        const bulkEvent: TBulkEventWithoutStatus = {
          metadata: completeEventMetadata,
          type: 'BUNDLE_QC_COMPLETE_EVENT',
          events: [
            {
              actionType: 'BUNDLE_QC_COMPLETE_EVENT',
              type: 'BUNDLE_QC_COMPLETE_EVENT',
              eventInfo: {
                bctxDetails: {
                  businessContextDetailsWithoutSize: selectedBctxInfo.bctxInfoWithoutSize.list,
                  size: selectedBctxInfo.size,
                },
                v_id: EventUtil.generateRandomId(eventTime),
                eventTime,
                bundleQty,
              },
            },
          ],
        };
        return [bulkEvent];
      }),
    );
  }

  getMissingEvent(missingQty: number, selectedBctxInfo: TSelectedBctxInfo, bundleQty: number) {
    const eventTime = new Date().getTime();
    return this.inputAppClientDBFacade.getBulkEventMetadata(eventTime, 'ACROSS_BCTX').pipe(
      take(1),
      map((bulkEventMetadata) => {
        if (missingQty > 0) {
          const bulkEvent: TBulkEventWithoutStatus = {
            metadata: bulkEventMetadata,
            type: 'BUNDLE_QC_MISSING_EVENT',
            events: [
              {
                actionType: 'BUNDLE_QC_MISSING_EVENT',
                type: 'BUNDLE_QC_MISSING_EVENT',
                eventInfo: {
                  bctxDetails: {
                    businessContextDetailsWithoutSize: selectedBctxInfo.bctxInfoWithoutSize.list,
                    size: selectedBctxInfo.size,
                  },
                  v_id: EventUtil.generateRandomId(eventTime),
                  eventTime,
                  missingQty,
                  bundleQty,
                },
              },
            ],
          };
          return [bulkEvent];
        } else return [];
      }),
    );
  }

  clearBctxAndAddEvents(missingQty: number, selectedBctxInfo: TSelectedBctxInfo, bundleQty: number) {
    combineLatest([
      this.getCompleteEvent(selectedBctxInfo, bundleQty),
      this.getMissingEvent(missingQty, selectedBctxInfo, bundleQty),
      this.getClearedIncompleteBundlesInfo(selectedBctxInfo.bctxInfoWithoutSize),
    ])
      .pipe(take(1))
      .subscribe((result) => {
        this.inputAppClientDBFacade.acquireLockAndThenAddBulkEvents(
          [...result[0], ...result[1]],
          false,
          {
            clearSelectedBctxAndUpdateIncompleteBundles: {
              incompleteBundlesInfo: result[2],
            },
            updateActualsOnly: false,
          },
          { syncIncompleteBundles: { closeModal: false } },
        );
      });
  }

  addEvents(
    updateBundlesInfo: boolean,
    updateActualsOnly: boolean,
    bulkEventsWithoutStatus: TBulkEventWithoutStatus[],
    bctxList: { list: TBctxItemWithoutSize[]; bctxKey: string; size: string }[],
    sizeActions?: TEventAddtionSideActions,
  ) {
    if (updateBundlesInfo) {
      this.getUpdatedIncompleteBundlesInfo(bctxList)
        .pipe(take(1))
        .subscribe((updatedIncompleteBundlesInfo) => {
          this.inputAppClientDBFacade.acquireLockAndThenAddBulkEvents(
            bulkEventsWithoutStatus,
            false,
            { updateIncompleteBundles: updatedIncompleteBundlesInfo, updateActualsOnly },
            sizeActions,
          );
        });
    } else {
      this.inputAppClientDBFacade.acquireLockAndThenAddBulkEvents(
        bulkEventsWithoutStatus,
        false,
        { updateActualsOnly },
        sizeActions,
      );
    }
  }

  addFtt(list: TBctxItemWithoutSize[], bctxKey: string, count: number, size: string, updateBundlesInfo: boolean) {
    if (updateBundlesInfo) {
      this.getUpdatedIncompleteBundlesInfo([{ list, bctxKey, size }])
        .pipe(take(1))
        .subscribe((updatedIncompleteBundlesInfo) => {
          this.inputAppClientDBFacade.addFtt(
            size,
            { list, bctxKey: bctxKey },
            count,
            {
              updateActualsOnly: false,
              updateIncompleteBundles: updatedIncompleteBundlesInfo,
            },
            { syncIncompleteBundles: { closeModal: false } },
          );
        });
    } else {
      this.inputAppClientDBFacade.addFtt(size, { list, bctxKey: bctxKey }, count, {
        updateActualsOnly: false,
      });
    }
  }
}
